import { useCallback } from 'react';

import { useUserConfig } from '@/features/_shared/user';
import type { ChartAppearance } from '@/features/charts/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/features/charts/types/highcharts';

import createRangeSelector from '../useRangeSelector';
import useChartFunctions from './useChartFunctions';

const DEFAULT_TIMEZONE = 'UTC';

const useBaseChartOptions = (
  height: number,
  appearance: ChartAppearance,
  prefersReducedMotion: boolean
) => {
  const { data: userConfigData } = useUserConfig();
  const timezone = userConfigData?.timezone.value ?? DEFAULT_TIMEZONE;
  const { createNavigator, createPlotOptions, createYAxis, createXAxis } = useChartFunctions(
    appearance,
    prefersReducedMotion
  );

  const createBaseChartOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[]): ExtendedOptions => ({
      accessibility: { enabled: false },
      time: { timezone },
      title: {
        text: undefined
      },
      colors: [...appearance.palette],
      chart: {
        reflow: true,
        animation: false,
        type: 'line',
        backgroundColor: appearance.background,
        height,
        style: {
          fontFamily: appearance.fontFamily
        },
        zooming: {
          type: 'x'
        }
      },
      exporting: {
        scale: 1,
        enabled: true,
        filename: 'GX-Chart',
        chartOptions: {
          legend: {
            enabled: true,
            layout: 'horizontal',
            itemStyle: {
              color: appearance.foreground,
              fontSize: '10px'
            }
          },
          xAxis: {
            labels: {
              style: {
                fontSize: '9px'
              }
            }
          },
          yAxis: {
            labels: {
              style: {
                fontSize: '9px'
              }
            }
          },
          plotOptions: {
            series: {
              dataLabels: {
                enabled: false
              }
            }
          },
          navigator: {
            enabled: false
          },
          rangeSelector: { enabled: false },
          scrollbar: {
            enabled: false
          }
        }
      },
      xAxis: createXAxis(),
      yAxis: createYAxis(),
      plotOptions: createPlotOptions(),
      tooltip: prefersReducedMotion ? { animation: false } : undefined,
      series: chartSeries,
      legend: { enabled: false },
      navigator: createNavigator(),
      rangeSelector: createRangeSelector(appearance, chartSeries),
      credits: {
        enabled: false
      }
    }),
    [
      appearance,
      createNavigator,
      createPlotOptions,
      createXAxis,
      createYAxis,
      height,
      prefersReducedMotion,
      timezone
    ]
  );

  return { createBaseChartOptions };
};

export default useBaseChartOptions;
